import React from 'react';
import {Typography} from '../typography';
import {Box} from '../box';
import {Button} from '../button/button';
import {UnstyledHtmlAnchor} from '../link-renderer';
import {FrameworkImage} from '../image-renderer';
import {HorizontalDivider, VerticalDivider} from '../divider';
import {VisibilityBox} from '../box/visibility-box';
import {InstagramIcon} from '../icons/instagram';
import {TwitterIcon} from '../icons/twitter';
import {YouTubeIcon} from '../icons/youtube';
import {ArrowRightIcon} from '../icons';

interface SocialItemProps {
  id: string;
  Icon: any;
  link?: string | null;
  handle?: string | null;
}

export interface ArtistAboutDetailsProps {
  thumbnail?: string | null;
  artistName?: string | null;
  numSupporters?: number;
  bio?: string | null;
  instagramHref?: string | null;
  twitterHref?: string | null;
  youtubeHref?: string | null;
  instagramHandle?: string | null;
  twitterHandle?: string | null;
  youtubeHandle?: string | null;
  withGoToProfileButton?: boolean;
  fontSizeArtistDescription?: number;
  navigateToProfile?: () => void;
}

export const ArtistAboutDetails: React.FC<ArtistAboutDetailsProps> = ({
  thumbnail,
  artistName,
  numSupporters,
  bio,
  instagramHandle,
  twitterHandle,
  youtubeHandle,
  withGoToProfileButton,
  instagramHref,
  twitterHref,
  youtubeHref,
  fontSizeArtistDescription,
  navigateToProfile,
}) => {
  // Href is coming from the api, handle is coming from dato.
  const socialInfo: SocialItemProps[] = [
    {
      id: 'instagram',
      Icon: InstagramIcon,
      handle: instagramHandle || instagramHref?.split('/').slice(-2)?.[0],
      link: instagramHandle
        ? `https://www.instagram.com/accounts/login/?next=/${instagramHandle}/`
        : instagramHref,
    },
    {
      id: 'twitter',
      Icon: TwitterIcon,
      handle: twitterHandle || twitterHref?.split('/').pop(),
      link: twitterHandle
        ? `https://twitter.com/${twitterHandle}`
        : twitterHref,
    },
    {
      id: 'youtube',
      Icon: YouTubeIcon,
      handle: youtubeHandle || youtubeHref?.split('/').pop(),
      link: youtubeHandle
        ? `https://www.youtube.com/${youtubeHandle}`
        : youtubeHref,
    },
  ];

  return (
    <>
      <HorizontalDivider />
      <Box
        flexDirection={['column', 'row', 'row', 'row']}
        borderWidth="one"
        borderColor="gray"
        borderStyle="solid"
        borderRadius="four"
        padding="two"
      >
        <Box>
          <Box
            width={['100%', '240px']}
            height={['auto', '240px']}
            css={{paddingBottom: '100%'}}
            borderColor="outlines"
            borderWidth="one"
            borderStyle="solid"
            borderRadius="circle"
            backgroundColor="surfaceDark"
            overflow="hidden"
          >
            {thumbnail ? (
              <FrameworkImage
                src={thumbnail}
                alt={artistName || undefined}
                layout="fill"
                objectFit="contain"
              />
            ) : null}
          </Box>
          <VisibilityBox show={[true, false]}>
            <HorizontalDivider />
          </VisibilityBox>
        </Box>
        <VisibilityBox show={[false, true]}>
          <VerticalDivider />
        </VisibilityBox>
        <Box flex="2">
          <Box
            marginLeft="lg"
            marginBottom="lg"
            width="auto"
            position="relative"
            paddingTop="ten"
          >
            <Box marginBottom="three">
              <Box mb="one">
                <Typography
                  color="textDefault"
                  fontSize="heroSmall"
                  textStyle="s"
                >
                  {artistName}
                </Typography>
              </Box>
              {numSupporters ? (
                <Typography color="textLow" textStyle="s">
                  {`${numSupporters} Supporter${
                    numSupporters !== 1 ? 's' : ''
                  }`}
                </Typography>
              ) : null}
            </Box>
            <Box
              width="100px"
              style={{position: 'absolute', right: '10px', top: '45px'}}
              flexDirection="row"
            >
              {socialInfo
                .filter((item) => !!item.link)
                .map(({id: socialId, Icon, handle, link}) => {
                  return (
                    <Box key={socialId} flex="1">
                      <UnstyledHtmlAnchor href={link as string} target="_blank">
                        <Box flexDirection="row" pb="one">
                          <Box>
                            <Icon color="outlines" size={18} />
                          </Box>
                        </Box>
                      </UnstyledHtmlAnchor>
                    </Box>
                  );
                })}
            </Box>
            <Box
              pr={['zero', 'zero', 'zero', 'eight']}
              paddingY={['seven', 'seven', 'seven', 'zero']}
            >
              <Typography
                color="textDefault"
                // css={{
                //   lineHeight: `${fontSizeArtistDescription}px`,
                //   fontSize: `${fontSizeArtistDescription}px`,
                // }}
                textStyle="s"
              >
                {bio}
              </Typography>
            </Box>
          </Box>
          {withGoToProfileButton && (
            <Box>
              <HorizontalDivider />
              <Button
                type="secondary"
                onPress={navigateToProfile}
                leftAccessory={<ArrowRightIcon color="outlines" />}
              >
                Go to profile
              </Button>
            </Box>
          )}
        </Box>
      </Box>
      <VisibilityBox show={[false, false, true]}>
        <HorizontalDivider />
      </VisibilityBox>
    </>
  );
};
